<script setup>
import BaseIcon from "@/app/components/ui/BaseIcon.vue"
import { useI18n } from "@/app/composables/useI18n"
import { router } from "@/app/router"
import { nextTick, ref, watch, watchEffect } from "vue"
import VueHorizontal from "vue-horizontal"
import ProviderCard from "./ProviderCard.vue"
import "@splidejs/vue-splide/css"

const props = defineProps({
  providers: {
    type: Array,
    required: true,
  },
  currentPage: {
    type: Number,
    required: true,
  },
  lastPage: {
    type: Number,
    required: true,
  },
})

const emit = defineEmits(["update:data"])
const horizontal = ref(null)
const currentScrollPosition = ref(0)

function updateData(page) {
  emit("update:data", page)
}

const { t } = useI18n()

function handleViewAll() {
  router.push({ path: "/casino/home/providers" })
}

function saveScrollPosition() {
  if (horizontal.value) {
    const scrollContainer = horizontal.value.$el.querySelector(".vue-horizontal")
    if (scrollContainer) {
      currentScrollPosition.value = scrollContainer.scrollLeft
    }
  }
}

watchEffect(() => {
  if (horizontal.value) {
    setTimeout(() => {
      const scrollContainer = horizontal.value?.$el?.querySelector(".vue-horizontal")
      if (scrollContainer) {
        scrollContainer.scrollLeft = currentScrollPosition.value
      }
    }, 100) // Adjust timeout if needed
  }
})

async function restoreScrollPosition() {
  // Wait for the DOM to update with new items
  await nextTick()
  await nextTick() // Sometimes a second nextTick is needed

  if (horizontal.value) {
    const scrollContainer = horizontal.value.$el
    if (scrollContainer && currentScrollPosition.value > 0) {
      // Use setTimeout to ensure the browser has time to process the DOM changes
      setTimeout(() => {
        if (scrollContainer) {
          scrollContainer.scrollLeft = currentScrollPosition.value
        }
      }, 50)
    }
  }
}

watch(() => props.providers.length, async (newLength, oldLength) => {
  if (newLength > oldLength && currentScrollPosition.value > 0) {
    await restoreScrollPosition()
  }
})

function prevSlide(event) {
  if (horizontal.value && typeof horizontal.value.prev === "function") {
    horizontal.value.prev(event)
  }
}

function nextSlide(event) {
  if (horizontal.value && typeof horizontal.value.next === "function") {
    horizontal.value.next(event)
    const isLastSlide = !horizontal.value.hasNext
    if (props.currentPage < props.lastPage && isLastSlide) {
      saveScrollPosition()
      updateData(props.currentPage + 1)
    }
  }
}
</script>

<template>
  <section v-if="providers.length > 0" class="list-wrapper">
    <div class="list-header">
      <div class="list-title">
        <BaseIcon name="providers" size="20" color="var(--be-disable-state)" />
        <span>
          {{ t("casino.providers") }}
        </span>
      </div>
      <div
        class="list-view"
        @click="handleViewAll"
      >
        <div>{{ t("general.view-all") }}</div>
        <div :class="horizontal?.hasPrev ? '' : 'dim'" @click="(event) => prevSlide(event)">
          <img src="/assets/images/left-arrow-base-sliding.svg" alt="">
        </div>
        <div :class="!horizontal?.hasNext && currentPage === lastPage ? 'dim' : ''" @click="(event) => nextSlide(event)">
          <img src="/assets/images/right-arrow-base-sliding.svg" alt="">
        </div>
      </div>
    </div>

    <div class="base-slide">
      <VueHorizontal
        ref="horizontal"
        :button="false"
      >
        <div v-for="provider in providers" :key="provider.id">
          <ProviderCard :provider="provider" :is-lobby="true" />
        </div>
      </VueHorizontal>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.base-slide {
  padding: 0 16px 16px 16px;
}

.dim {
  opacity: 0.3;
}

@media screen and (max-width: 550px) {
  .base-slide {
    padding: 0px;
  }
}

.list-wrapper {
  margin: 10px 0px;
  background-color: var(--be-big-card-bg);
  border-radius: 8px;

  .list-header {
    display: flex;
    justify-content: space-between;
    margin-right: 16px;
    margin-bottom: 10px;
    padding-top: 16px;
  }

  .list-view {
    display: flex;
    gap: 0 20px;
    align-items: center;

    font-size: 16px;
    cursor: pointer;
    color: var(--be-primary-btn);
    font-family: Rubik;
  }

  .list-title {
    display: flex;
    align-items: center;
    margin: 0 0 4px 19px;
    font-size: 16px;
    font-family: Rubik-med;
    color: var(--be-secondary-btn-label);

    img {
      max-width: 24px;
      max-width: 24px;
      height: 24px;
    }

    span {
      margin: 4px 0 0 8px;
    }
  }
}

@media screen and (max-width: 550px) {
  .list-wrapper {
    background-color: transparent;

    .list-header {
    margin-right: 0px;
  }
    .list-title {
      margin: 0 0 4px 0px;
    }
  }
}
</style>
